import {
  API_PREFIX,
  HttpError,
  paginatedFromDto,
  type HttpClient,
} from "@nasam/kernel";
import type { DepartmentsRepository } from "../domain";
import {
  archivedDepartmentFromDto,
  archivedDepartmentMatchFromDto,
  departmentCreateToDto,
  departmentFromDto,
  departmentUpdateToDto,
} from "./organizationMappers";
import {
  archivedDepartmentListResponseDtoSchema,
  archivedDepartmentMatchResponseDtoSchema,
  departmentCreateWriteDtoSchema,
  departmentListResponseDtoSchema,
  departmentResponseDtoSchema,
  departmentUpdateWriteDtoSchema,
  messageResponseDtoSchema,
} from "./organizationDtos";

const DEPARTMENTS_PATH = `${API_PREFIX}/departments`;

function isConflict(error: unknown): error is HttpError {
  return error instanceof HttpError && error.status === 409;
}

export function createDepartmentsRepository(
  http: HttpClient,
): DepartmentsRepository {
  return {
    async list(query) {
      const response = await http.get(DEPARTMENTS_PATH, { params: query });
      return paginatedFromDto(
        departmentListResponseDtoSchema.parse(response),
        departmentFromDto,
      );
    },

    async getById(id) {
      const response = await http.get(`${DEPARTMENTS_PATH}/${id}`);
      return departmentFromDto(departmentResponseDtoSchema.parse(response).data);
    },

    async create(input) {
      const dto = departmentCreateWriteDtoSchema.parse(
        departmentCreateToDto(input),
      );
      try {
        const response = await http.post(DEPARTMENTS_PATH, dto);
        const created = departmentResponseDtoSchema.parse(response);
        return {
          status: "created",
          department: departmentFromDto(created.data),
          ...(created.message === undefined
            ? {}
            : { responseMessage: created.message }),
        };
      } catch (error) {
        if (!isConflict(error)) throw error;
        // A 409 carrying code ARCHIVED_DEPARTMENT_FOUND offers a restore; a
        // plain 409 (a living department has the name) is just a duplicate.
        const archived = archivedDepartmentMatchResponseDtoSchema.safeParse(
          error.data,
        );
        if (archived.success) {
          return {
            status: "archived-match",
            match: archivedDepartmentMatchFromDto(archived.data.data),
            message: archived.data.message,
          };
        }
        return { status: "duplicate", message: error.message };
      }
    },

    async update(id, input) {
      const dto = departmentUpdateWriteDtoSchema.parse(
        departmentUpdateToDto(input),
      );
      try {
        const response = await http.patch(`${DEPARTMENTS_PATH}/${id}`, dto);
        const updated = departmentResponseDtoSchema.parse(response);
        return {
          status: "updated",
          department: departmentFromDto(updated.data),
          ...(updated.message === undefined
            ? {}
            : { responseMessage: updated.message }),
        };
      } catch (error) {
        if (!isConflict(error)) throw error;
        return { status: "duplicate", message: error.message };
      }
    },

    // Soft delete. A 409 (users still assigned) or 404 surfaces as HttpError
    // carrying the backend's localized message.
    async archive(id) {
      const response = await http.delete(`${DEPARTMENTS_PATH}/${id}`);
      messageResponseDtoSchema.parse(response);
    },

    async listArchived(query) {
      const response = await http.get(`${DEPARTMENTS_PATH}/archived`, {
        params: query,
      });
      return paginatedFromDto(
        archivedDepartmentListResponseDtoSchema.parse(response),
        archivedDepartmentFromDto,
      );
    },

    // A 409 here means a living department has since taken the name.
    async restore(id) {
      const response = await http.patch(`${DEPARTMENTS_PATH}/${id}/restore`);
      const restored = departmentResponseDtoSchema.parse(response);
      return {
        ...departmentFromDto(restored.data),
        ...(restored.message === undefined
          ? {}
          : { responseMessage: restored.message }),
      };
    },
  };
}
